import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Select, Input,
  IconButton, Tooltip, useToast, SimpleGrid, Stat, StatLabel, StatNumber, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Table, Thead, Tbody, Tr, Th, Td, Textarea, Alert, AlertIcon, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, InputGroup, InputLeftElement, Checkbox,
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
  AlertDialogOverlay, Image,
} from "@chakra-ui/react";
import {
  RefreshCw, Search, Settings, Zap, Trash2, ShoppingCart, CheckCircle, Package,
  Truck, AlertTriangle, ExternalLink, Pencil, X, ArrowRight, Undo2, XCircle,
  PackageCheck, Link2,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  getCoberturaFuros, moverFuroEstagio, gerarPedidosFuro, excluirPedidoFuro,
  formalizarPedidoFuro, atualizarItemPedidoFuro, removerItemPedidoFuro,
  adicionarItemPedidoFuro, atualizarPedidoFuro, getDetalheFuro,
  marcarPedidoFuroRetirado, desfazerPedidoFuroRetirado,
} from "../services/api";
import { formatBRL, formatDateTime } from "../utils/formatters";
import ConfiguracoesFornecedoresCotacao from "../components/ConfiguracoesFornecedoresCotacao";
import ConfirmDialog from "../components/ConfirmDialog";
import ModalColetaLalamove from "../components/LalamoveColeta/ModalColetaLalamove";
import SeloCorrida from "../components/LalamoveColeta/SeloCorrida";

const ACCENT = "#F68633";

// O card é o furo (o item a cobrir) e caminha do início ao fim do funil.
// A 4a coluna mostra os pedidos montados a partir dos furos aprovados.
const COLUNAS = [
  { key: "triagem", label: "A tratar", cor: "gray", ajuda: "Furos com resposta de fornecedor que você ainda não olhou." },
  { key: "selecionado", label: "Pode suprir", cor: "blue", ajuda: "Você já escolheu quais ofertas cobrem o furo." },
  { key: "aprovado", label: "Aprovados", cor: "green", ajuda: "Liberados para entrar num pedido." },
];

const PROXIMO = { triagem: "selecionado", selecionado: "aprovado" };
const ANTERIOR = { selecionado: "triagem", aprovado: "selecionado", descartado: "triagem" };

const corPrioridade = (p) => (p === 1 ? "red" : p === 2 ? "orange" : p === 3 ? "gray" : "blue");

// Selo de prazo de despacho a partir de prazo_envio/dias_prazo que o board devolve.
const seloPrazo = (furo) => {
  if (!furo.prazo_envio) return null;
  const dias = furo.dias_prazo;
  if (furo.atrasado) {
    const atraso = Math.abs(dias || 0);
    return { cor: "red", texto: atraso >= 1 ? `atrasado ${atraso}d` : "atrasado" };
  }
  if (dias === 0) {
    const hora = new Date(furo.prazo_envio).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return { cor: "orange", texto: `vence hoje ${hora}` };
  }
  return { cor: dias <= 2 ? "yellow" : "gray", texto: `${dias}d` };
};

// Quanto a oferta do fornecedor está acima (ou abaixo) do que a peça nos custa
// normalmente — é o que diz se vale a pena comprar para cobrir o furo.
const compararCusto = (preco, custo) => {
  if (preco == null || !custo || custo <= 0) return null;
  const dif = preco - custo;
  const pct = Math.round((dif / custo) * 100);
  return {
    dif,
    pct,
    cor: pct <= 0 ? "green" : pct <= 20 ? "gray" : pct <= 50 ? "orange" : "red",
    texto: `${pct > 0 ? "+" : ""}${pct}%`,
    detalhe: `Oferta ${formatBRL(preco)} · custo ${formatBRL(custo)} `
      + `(${dif >= 0 ? "+" : "-"}${formatBRL(Math.abs(dif))})`,
  };
};

export default function CoberturaFuros() {
  const toast = useToast();
  const cancelRef = useRef();

  const [dados, setDados] = useState({ furos: [], pedidos: [], resumo: {} });
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [dias, setDias] = useState(30);
  const [busca, setBusca] = useState("");
  const [incluirResolvidos, setIncluirResolvidos] = useState(false);
  const [somenteNoPrazo, setSomenteNoPrazo] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [furoDetalhe, setFuroDetalhe] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [pedidoAberto, setPedidoAberto] = useState(null);
  // Coleta Lalamove (#2307): {compra} para pedir, {ordemId} para abrir a corrida.
  const [coleta, setColeta] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await getCoberturaFuros({
      dias,
      busca: busca || undefined,
      incluir_resolvidos: incluirResolvidos,
      somente_no_prazo: somenteNoPrazo,
      data_inicio: dataInicio || undefined,
      data_fim: dataFim || undefined,
    });
    if (r) setDados({ furos: r.furos || [], pedidos: r.pedidos || [], resumo: r.resumo || {} });
    else toast({ title: "Erro ao carregar o quadro", status: "error", isClosable: true });
    setLoading(false);
  }, [dias, busca, incluirResolvidos, somenteNoPrazo, dataInicio, dataFim, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  // Só entra no quadro o furo que tem resposta de fornecedor.
  const colunas = useMemo(() => {
    const base = { triagem: [], selecionado: [], aprovado: [] };
    (dados.furos || []).forEach((furo) => {
      if ((furo.ofertas || []).length === 0) return;
      (base[furo.estagio] || base.triagem).push(furo);
    });
    return base;
  }, [dados.furos]);

  const furosSemOferta = useMemo(
    () => (dados.furos || []).filter((f) => (f.ofertas || []).length === 0),
    [dados.furos]
  );

  // Ofertas aprovadas fora de pedido: o que ainda dá para puxar para um rascunho.
  const ofertasAprovadas = useMemo(() => {
    const lista = [];
    (dados.furos || []).forEach((furo) => {
      (furo.ofertas || []).forEach((o) => {
        if (o.estagio === "aprovado" && !o.pedido) lista.push({ ...o, furo });
      });
    });
    return lista;
  }, [dados.furos]);

  const rascunhos = (dados.pedidos || []).filter((p) => p.status === "rascunho");
  const formalizados = (dados.pedidos || []).filter((p) => p.status === "formalizado");
  const retirados = (dados.pedidos || []).filter((p) => p.status === "retirado");

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    const destino = destination.droppableId;
    const furoId = Number(draggableId);
    const anterior = dados;

    // Atualização otimista.
    setDados((d) => ({
      ...d,
      furos: d.furos.map((f) => (f.id === furoId ? { ...f, estagio: destino } : f)),
    }));

    const r = await moverFuroEstagio(furoId, destino);
    if (!r?.success) {
      setDados(anterior);
      toast({ title: r?.error || "Não foi possível mover o furo", status: "warning", isClosable: true });
      // Falhou por falta de escolha: abre o card já na seleção.
      if ((r?.error || "").includes("marque")) setFuroDetalhe(furoId);
    } else {
      carregar();
    }
  };

  const gerar = async () => {
    // Montar de novo refaz todos os rascunhos — avisa antes de perder edições manuais.
    if (rascunhos.length > 0) {
      const ok = window.confirm(
        `Já existem ${rascunhos.length} pedido(s) em rascunho. Montar de novo refaz todos eles ` +
        `e descarta as edições manuais. Os pedidos já formalizados não são afetados.\n\nContinuar?`
      );
      if (!ok) return;
    }
    setGerando(true);
    const r = await gerarPedidosFuro();
    setGerando(false);
    if (r?.success) {
      toast({
        title: `${r.pedidos_gerados} pedido(s) montado(s) com ${r.itens} item(ns)`,
        description: r.economia_frete > 0
          ? `Agrupar os itens economizou ${formatBRL(r.economia_frete)} de frete.`
          : "Nenhuma economia de frete a fazer com estes itens.",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
      carregar();
    } else {
      toast({ title: r?.error || "Erro ao montar os pedidos", status: "error", isClosable: true });
    }
  };

  const descartarPedido = async (pedido) => {
    const r = await excluirPedidoFuro(pedido.id);
    if (r?.success) {
      toast({ title: "Pedido descartado — os furos voltaram para Aprovados", status: "info" });
      setPedidoAberto(null);
      carregar();
    } else {
      toast({ title: r?.error || "Erro ao descartar", status: "error", isClosable: true });
    }
  };

  const formalizar = async (pedido) => {
    const r = await formalizarPedidoFuro(pedido.id);
    if (r?.success) {
      toast({
        title: "Pedido formalizado",
        description: `${r.vendas_atualizadas} pedido(s) de venda confirmado(s) e sem o marcador de furo.`,
        status: "success",
        duration: 7000,
        isClosable: true,
      });
      setPedidoAberto(null);
      carregar();
    } else {
      toast({ title: r?.error || "Erro ao formalizar", status: "error", isClosable: true });
    }
  };

  const resumo = dados.resumo || {};

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align="center" mb={2} gap={3} flexWrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color="#01695E">Cobertura de Furos</Text>
          <Text fontSize="sm" color="gray.500">
            Cada card é um item a cobrir. Abra, marque quais fornecedores servem e avance até o pedido.
          </Text>
        </Box>
        <HStack flexWrap="wrap">
          <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
          <Button size="sm" variant="outline" leftIcon={<Settings size={15} />} onClick={() => setConfigOpen(true)}>
            Fornecedores (frete e prioridade)
          </Button>
          <Button size="sm" colorScheme="orange" leftIcon={<Zap size={15} />} onClick={gerar} isLoading={gerando}>
            Montar pedidos
          </Button>
        </HStack>
      </Flex>

      <HStack mb={4} spacing={2} flexWrap="wrap">
        <InputGroup size="sm" maxW="280px" bg="white">
          <InputLeftElement pointerEvents="none"><Search size={14} color="#A0AEC0" /></InputLeftElement>
          <Input
            placeholder="Produto, código ou pedido"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") carregar(); }}
            borderRadius="md"
          />
        </InputGroup>
        <Select size="sm" maxW="150px" bg="white" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
          <option value={7}>Cotados: 7 dias</option>
          <option value={15}>Cotados: 15 dias</option>
          <option value={30}>Cotados: 30 dias</option>
          <option value={60}>Cotados: 60 dias</option>
          <option value={90}>Cotados: 90 dias</option>
        </Select>

        <HStack spacing={1} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1}>
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Venda de</Text>
          <Input
            size="xs"
            type="date"
            maxW="140px"
            variant="unstyled"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
          <Text fontSize="xs" color="gray.500">até</Text>
          <Input
            size="xs"
            type="date"
            maxW="140px"
            variant="unstyled"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
          {(dataInicio || dataFim) && (
            <IconButton
              aria-label="Limpar datas"
              size="xs"
              variant="ghost"
              icon={<X size={13} />}
              onClick={() => { setDataInicio(""); setDataFim(""); }}
            />
          )}
        </HStack>

        <Checkbox
          size="sm"
          colorScheme="green"
          isChecked={somenteNoPrazo}
          onChange={(e) => setSomenteNoPrazo(e.target.checked)}
        >
          <Text fontSize="sm">Apenas pedidos ainda no prazo</Text>
        </Checkbox>
        <Checkbox
          size="sm"
          isChecked={incluirResolvidos}
          onChange={(e) => setIncluirResolvidos(e.target.checked)}
        >
          <Text fontSize="sm">Mostrar furos já resolvidos</Text>
        </Checkbox>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4, xl: 7 }} spacing={3} mb={5}>
        <CardStat label="Furos em aberto" valor={resumo.furos ?? 0} cor="#01695E" />
        <CardStat label="Ainda a tratar" valor={resumo.a_tratar ?? 0} cor="#2B6CB0" />
        <CardStat label="Fora do prazo" valor={resumo.furos_atrasados ?? 0} cor="#C53030" />
        <CardStat label="Sem nenhuma oferta" valor={resumo.furos_sem_oferta ?? 0} cor="#B7791F" />
        <CardStat label="Aprovados" valor={resumo.aprovados ?? 0} cor="#2F855A" />
        <CardStat label="Pedidos em rascunho" valor={rascunhos.length} cor={ACCENT} />
        <CardStat label="A retirar" valor={formalizados.length} cor="#2C7A7B" />
      </SimpleGrid>

      {loading ? (
        <Center py={20}><Spinner size="xl" color={ACCENT} thickness="4px" /></Center>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={3} alignItems="start">
            {COLUNAS.map((col) => (
              <Droppable droppableId={col.key} key={col.key}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    bg={snapshot.isDraggingOver ? "orange.50" : "gray.100"}
                    borderWidth="1px"
                    borderColor={snapshot.isDraggingOver ? "orange.200" : "gray.200"}
                    borderRadius="xl"
                    p={2}
                    minH="200px"
                    transition="background 0.12s"
                  >
                    <HStack mb={1} px={1} justify="space-between">
                      <HStack spacing={2}>
                        <Box w="8px" h="8px" borderRadius="full" bg={`${col.cor}.400`} />
                        <Text fontWeight="bold" fontSize="sm">{col.label}</Text>
                      </HStack>
                      <Badge colorScheme={col.cor} borderRadius="full">{colunas[col.key].length}</Badge>
                    </HStack>
                    <Text fontSize="11px" color="gray.500" px={1} mb={2}>{col.ajuda}</Text>

                    <VStack align="stretch" spacing={2}>
                      {colunas[col.key].map((furo, idx) => (
                        <Draggable draggableId={String(furo.id)} index={idx} key={furo.id}>
                          {(prov, snap) => (
                            <Box
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              bg="white"
                              borderWidth="1px"
                              borderColor={furo.ofertas.some((o) => o.pedido) ? "orange.300" : "gray.100"}
                              borderLeftWidth="4px"
                              borderLeftColor={`${col.cor}.400`}
                              borderRadius="lg"
                              p={3}
                              boxShadow={snap.isDragging ? "lg" : "xs"}
                              cursor="pointer"
                              _hover={{ boxShadow: "md", borderColor: "orange.200" }}
                              onClick={() => setFuroDetalhe(furo.id)}
                            >
                              <CardFuro furo={furo} />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {colunas[col.key].length === 0 && (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={6}>
                          {col.key === "triagem" ? "tudo tratado 🎉" : "—"}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                )}
              </Droppable>
            ))}

            {/* 4a coluna: os pedidos montados por fornecedor */}
            <Box bg="gray.100" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={2} minH="200px">
              <HStack mb={1} px={1} justify="space-between">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" borderRadius="full" bg="orange.400" />
                  <Text fontWeight="bold" fontSize="sm">Pedidos pré-formalizados</Text>
                </HStack>
                <Badge colorScheme="orange" borderRadius="full">{rascunhos.length}</Badge>
              </HStack>
              <Text fontSize="11px" color="gray.500" px={1} mb={2}>
                Ainda não combinado com o fornecedor. Clique para editar.
              </Text>

              <VStack align="stretch" spacing={2}>
                {rascunhos.map((pedido) => (
                  <CardPedido
                    key={pedido.id}
                    pedido={pedido}
                    onAbrir={() => setPedidoAberto(pedido)}
                    onLalamove={() => setColeta({ compra: { tipo: "furo", id: pedido.id } })}
                    onAbrirCorrida={(ordemId) => setColeta({ ordemId })}
                  />
                ))}
                {rascunhos.length === 0 && (
                  <Box textAlign="center" py={6} px={2}>
                    <Text fontSize="xs" color="gray.400">
                      Aprove os furos e clique em <b>Montar pedidos</b>.
                    </Text>
                  </Box>
                )}

                {formalizados.length > 0 && (
                  <>
                    <Divider my={2} />
                    <HStack px={1} justify="space-between">
                      <Text fontWeight="bold" fontSize="xs" color="green.700">Formalizados — a retirar</Text>
                      <Badge colorScheme="green" borderRadius="full">{formalizados.length}</Badge>
                    </HStack>
                    {formalizados.slice(0, 15).map((pedido) => (
                      <CardPedido
                    key={pedido.id}
                    pedido={pedido}
                    onAbrir={() => setPedidoAberto(pedido)}
                    onLalamove={() => setColeta({ compra: { tipo: "furo", id: pedido.id } })}
                    onAbrirCorrida={(ordemId) => setColeta({ ordemId })}
                  />
                    ))}
                  </>
                )}
              </VStack>
            </Box>

            {/* 5a coluna: a mercadoria já saiu do fornecedor */}
            <Box bg="gray.100" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={2} minH="200px">
              <HStack mb={1} px={1} justify="space-between">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" borderRadius="full" bg="teal.400" />
                  <Text fontWeight="bold" fontSize="sm">Pedidos retirados</Text>
                </HStack>
                <Badge colorScheme="teal" borderRadius="full">{retirados.length}</Badge>
              </HStack>
              <Text fontSize="11px" color="gray.500" px={1} mb={2}>
                Já saiu do fornecedor — com o rastreio da entrega.
              </Text>

              <VStack align="stretch" spacing={2}>
                {retirados.map((pedido) => (
                  <CardPedido
                    key={pedido.id}
                    pedido={pedido}
                    onAbrir={() => setPedidoAberto(pedido)}
                    onLalamove={() => setColeta({ compra: { tipo: "furo", id: pedido.id } })}
                    onAbrirCorrida={(ordemId) => setColeta({ ordemId })}
                  />
                ))}
                {retirados.length === 0 && (
                  <Box textAlign="center" py={6} px={2}>
                    <Text fontSize="xs" color="gray.400">
                      Abra um pedido formalizado e marque <b>Pedido retirado</b>.
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          </SimpleGrid>
        </DragDropContext>
      )}

      {!loading && furosSemOferta.length > 0 && (
        <Accordion allowToggle mt={6} bg="white" borderRadius="lg" borderWidth="1px">
          <AccordionItem border="none">
            <AccordionButton>
              <HStack flex="1" textAlign="left" spacing={2}>
                <AlertTriangle size={16} color="#C53030" />
                <Text fontWeight="semibold" fontSize="sm">
                  {furosSemOferta.length} furo(s) sem nenhuma oferta de fornecedor
                </Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <Box overflowX="auto" maxH="320px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Produto</Th>
                      <Th>Código</Th>
                      <Th isNumeric>Qtd</Th>
                      <Th>Prazo</Th>
                      <Th>Pedido</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {furosSemOferta.map((f) => {
                      const prazo = seloPrazo(f);
                      return (
                      <Tr key={f.id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => setFuroDetalhe(f.id)}>
                        <Td fontSize="xs">{f.nome_produto}</Td>
                        <Td fontSize="xs" color="gray.500">{f.codigo_global}</Td>
                        <Td fontSize="xs" isNumeric>{f.quantidade}</Td>
                        <Td>
                          {prazo
                            ? <Badge colorScheme={prazo.cor} fontSize="9px">{prazo.texto}</Badge>
                            : <Text fontSize="xs" color="gray.400">—</Text>}
                        </Td>
                        <Td fontSize="xs">
                          {f.venda ? (
                            <Button
                              as="a"
                              href={`/vendas?q=${encodeURIComponent(f.pedido_origem || "")}`}
                              target="_blank"
                              size="xs"
                              variant="link"
                              colorScheme="blue"
                              rightIcon={<ExternalLink size={11} />}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {f.pedido_origem}
                            </Button>
                          ) : (
                            <Text color="gray.400">{f.pedido_origem || "—"}</Text>
                          )}
                        </Td>
                      </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      <ModalPedido
        pedido={pedidoAberto}
        ofertasAprovadas={ofertasAprovadas}
        onClose={() => setPedidoAberto(null)}
        onMudou={carregar}
        onSubstituirPedido={(p) => setPedidoAberto(p)}
        onDescartar={(p) => setConfirmar({ tipo: "descartar", pedido: p })}
        onFormalizar={(p) => setConfirmar({ tipo: "formalizar", pedido: p })}
        onLalamove={(p) => setColeta(p.lalamove?.ativa ? { ordemId: p.lalamove.ordem_id } : { compra: { tipo: "furo", id: p.id } })}
      />

      <ModalColetaLalamove
        isOpen={!!coleta}
        onClose={() => setColeta(null)}
        compra={coleta?.compra || null}
        ordemId={coleta?.ordemId || null}
        onMudou={() => { carregar(); setPedidoAberto(null); }}
      />

      <ModalDetalheFuro
        furoId={furoDetalhe}
        onClose={() => setFuroDetalhe(null)}
        onMudou={carregar}
      />

      <ConfiguracoesFornecedoresCotacao
        isOpen={configOpen}
        onClose={() => { setConfigOpen(false); carregar(); }}
      />

      <AlertDialog isOpen={!!confirmar} leastDestructiveRef={cancelRef} onClose={() => setConfirmar(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmar?.tipo === "formalizar" ? "Confirmar pedido com o fornecedor" : "Descartar pedido"}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              {confirmar?.tipo === "formalizar" ? (
                <>
                  Confirma que o pedido foi fechado com <b>{confirmar?.pedido?.fornecedor_nome}</b>?
                  <br /><br />
                  Cada pedido de venda vai receber uma observação dizendo onde a peça foi comprada,
                  volta para <b>confirmado</b> e perde o marcador de <b>furo de estoque</b>.
                  <br /><br />
                  <Text as="span" color="gray.500">Esta ação não pode ser desfeita por aqui.</Text>
                </>
              ) : (
                <>O pedido some e os furos voltam para a coluna <b>Aprovados</b>.</>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmar(null)} size="sm">Cancelar</Button>
              <Button
                colorScheme={confirmar?.tipo === "formalizar" ? "green" : "red"}
                ml={3}
                size="sm"
                onClick={() => {
                  const acao = confirmar.tipo === "formalizar" ? formalizar : descartarPedido;
                  const alvo = confirmar.pedido;
                  setConfirmar(null);
                  acao(alvo);
                }}
              >
                {confirmar?.tipo === "formalizar" ? "Sim, foi formalizado" : "Descartar"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

/**
 * Dossiê do furo e, principalmente, onde se escolhe quem cobre: marca as ofertas
 * e confirma — o card avança de coluna.
 */
function ModalDetalheFuro({ furoId, onClose, onMudou }) {
  const toast = useToast();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [marcadas, setMarcadas] = useState([]);

  useEffect(() => {
    if (!furoId) { setDados(null); setMarcadas([]); return; }
    let ativo = true;
    setCarregando(true);
    getDetalheFuro(furoId).then((r) => {
      if (!ativo) return;
      if (r) {
        setDados(r);
        setMarcadas((r.ofertas || []).filter((o) => o.selecionada).map((o) => o.id));
      } else {
        toast({ title: "Erro ao carregar o pedido", status: "error", isClosable: true });
      }
      setCarregando(false);
    });
    return () => { ativo = false; };
  }, [furoId, toast]);

  const alternar = (ofertaId) => {
    setMarcadas((m) => (m.includes(ofertaId) ? m.filter((i) => i !== ofertaId) : [...m, ofertaId]));
  };

  const salvar = async (estagio) => {
    setSalvando(true);
    const r = await moverFuroEstagio(furoId, estagio, marcadas);
    setSalvando(false);
    if (r?.success) {
      // Sem cotação aceita o pedido vai para CANCELADA sozinho — quem descartou
      // precisa saber para cancelar no canal de venda.
      const pedido = r.venda?.id_origem || (r.venda ? `#${r.venda.id}` : "");
      if (r.venda?.acao === "cancelada") {
        toast({
          title: `Pedido ${pedido} marcado como CANCELADA`,
          description: "Nenhuma cotação aceita. O motivo já está nas observações do pedido — siga com o cancelamento no canal de venda.",
          status: "warning",
          duration: 9000,
          isClosable: true,
        });
      } else if (r.venda?.acao === "reaberta") {
        toast({
          title: `Cancelamento desfeito no pedido ${pedido}`,
          description: "O furo voltou para a fila de cotação e o Status TAGA voltou para CONFIRMADA.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      }
      onMudou();
      onClose();
    } else {
      toast({ title: r?.error || "Não foi possível salvar", status: "warning", isClosable: true });
    }
  };

  const v = dados?.venda;
  const furo = dados?.furo;
  const item = dados?.item_venda;
  const custo = item?.custo_unitario;
  const prazo = v ? seloPrazo({ prazo_envio: v.prazo_envio, atrasado: v.atrasado, dias_prazo: v.dias_prazo }) : null;

  const estagio = furo?.estagio || "triagem";
  const travado = !!furo?.comprado || !!furo?.em_pedido;
  const proximo = PROXIMO[estagio];
  const rotuloProximo = estagio === "triagem" ? "Confirmar seleção e avançar" : "Aprovar";
  const semOferta = (dados?.ofertas || []).length === 0;
  const rotuloEtapa = COLUNAS.find((c) => c.key === estagio)?.label
    || (estagio === "descartado" ? "Descartado" : estagio);

  return (
    <Modal isOpen={!!furoId} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#01695E" pb={2}>
          <HStack spacing={3} align="start">
            {item?.imagem_url && (
              <Image src={item.imagem_url} boxSize="52px" objectFit="contain" bg="gray.50" borderRadius="md" alt="" />
            )}
            <Box minW={0}>
              <Text fontSize="lg" noOfLines={2}>{item?.produto_nome || furo?.nome_produto || "Furo"}</Text>
              <HStack spacing={2} mt={1} flexWrap="wrap">
                {furo?.codigo_global && <Badge colorScheme="purple">{furo.codigo_global}</Badge>}
                {furo && <Badge colorScheme="orange" variant="outline">{furo.quantidade}x</Badge>}
                {custo != null && (
                  <Tooltip label="Custo de compra cadastrado para esta peça">
                    <Badge colorScheme="teal">custo {formatBRL(custo)}</Badge>
                  </Tooltip>
                )}
                {prazo && <Badge colorScheme={prazo.cor}>{prazo.texto}</Badge>}
                {furo && <Badge colorScheme={estagio === "aprovado" ? "green" : "gray"}>{rotuloEtapa}</Badge>}
              </HStack>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {carregando || !dados ? (
            <Center py={16}><Spinner color={ACCENT} /></Center>
          ) : (
            <VStack align="stretch" spacing={5}>
              {!v && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Não encontrei a venda de origem ({furo?.pedido_origem || "sem pedido"}).
                </Alert>
              )}

              {v && (
                <Box>
                  <HStack justify="space-between" mb={2} flexWrap="wrap">
                    <Text fontWeight="bold" fontSize="sm">Pedido de venda</Text>
                    <Button
                      as="a"
                      href={`/vendas?q=${encodeURIComponent(v.id_origem || "")}`}
                      target="_blank"
                      size="xs"
                      variant="outline"
                      colorScheme="blue"
                      rightIcon={<ExternalLink size={12} />}
                    >
                      Abrir em Vendas
                    </Button>
                  </HStack>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                    <Info label="Número" valor={v.id_origem || `#${v.id}`} />
                    <Info label="Canal" valor={v.origem || "—"} />
                    <Info label="Status" valor={v.status || "—"} />
                    <Info label="Data da venda" valor={formatDateTime(v.data_venda)} />
                    <Info label="Valor do pedido" valor={formatBRL(v.valor_total)} cor="#01695E" />
                    <Info
                      label="Despachar até"
                      valor={v.prazo_envio ? formatDateTime(v.prazo_envio) : "—"}
                      cor={v.atrasado ? "#C53030" : undefined}
                    />
                    <Info label="Status TAGA" valor={v.status_taga === 1 ? "Confirmada" : v.status_taga === 2 ? "Cancelada" : "Pendente"} />
                    <Info label="Marcador" valor={v.marcador === 1 ? "Furo de estoque" : v.marcador ? `#${v.marcador}` : "nenhum"} />
                  </SimpleGrid>

                  {v.cliente && (
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
                      <Info label="Cliente" valor={v.cliente.nome || v.cliente.nickname || "—"} />
                      <Info label="Documento" valor={v.cliente.documento || "—"} />
                      <Info label="Telefone" valor={v.cliente.telefone || "—"} />
                    </SimpleGrid>
                  )}
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Entrega:{" "}
                    {v.endereco
                      ? [v.endereco.cidade, v.endereco.uf, v.endereco.cep].filter(Boolean).join(" · ")
                      : "endereço ainda não liberado pelo canal"}
                    {v.envio?.modo_envio ? ` · ${v.envio.modo_envio}` : ""}
                    {v.envio?.empresa ? ` (${v.envio.empresa})` : ""}
                    {v.envio?.codigo_rastreio ? ` · rastreio ${v.envio.codigo_rastreio}` : ""}
                  </Text>
                </Box>
              )}

              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Itens do pedido {dados.itens_pedido.length > 1 && (
                    <Text as="span" fontWeight="normal" color="gray.500">
                      — o furo é só um deles, os outros seguem normalmente
                    </Text>
                  )}
                </Text>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th></Th>
                        <Th>Produto</Th>
                        <Th>Código</Th>
                        <Th>Marca</Th>
                        <Th isNumeric>Qtd</Th>
                        <Th isNumeric>Custo</Th>
                        <Th isNumeric>Unitário</Th>
                        <Th isNumeric>Total</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dados.itens_pedido.map((it) => (
                        <Tr key={it.id} bg={it.e_o_furo ? "orange.50" : undefined} _hover={{ bg: it.e_o_furo ? "orange.100" : "gray.50" }}>
                          <Td px={2}>
                            {it.imagem_url
                              ? <Image src={it.imagem_url} boxSize="32px" objectFit="contain" alt="" />
                              : <Box boxSize="32px" bg="gray.100" borderRadius="sm" />}
                          </Td>
                          <Td fontSize="xs" maxW="260px">
                            <Text noOfLines={2}>{it.produto_nome || "—"}</Text>
                            {it.e_o_furo && <Badge colorScheme="orange" fontSize="9px" mt={1}>este é o furo</Badge>}
                          </Td>
                          <Td fontSize="xs" color="gray.500">{it.codigo_global || "—"}</Td>
                          <Td fontSize="xs">{it.marca || "—"}</Td>
                          <Td fontSize="xs" isNumeric>{it.quantidade}</Td>
                          <Td fontSize="xs" isNumeric color="gray.600">
                            {it.custo_unitario != null ? formatBRL(it.custo_unitario) : "—"}
                          </Td>
                          <Td fontSize="xs" isNumeric>{formatBRL(it.valor_unitario)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="semibold">{formatBRL(it.total)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {item?.codigo_fornecedor && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Código no fornecedor: <b>{item.codigo_fornecedor}</b>
                    {item.localizacao_estoque ? ` · Localização: ${item.localizacao_estoque}` : ""}
                    {furo?.cotado_em ? ` · Cotado em ${formatDateTime(furo.cotado_em)}` : ""}
                  </Text>
                )}
              </Box>

              <Box>
                <HStack justify="space-between" mb={2} flexWrap="wrap">
                  <Text fontWeight="bold" fontSize="sm">
                    Quem pode cobrir este furo ({dados.ofertas.length})
                  </Text>
                  {!travado && !semOferta && (
                    <Text fontSize="xs" color="gray.500">
                      Marque as ofertas que servem — mais de uma pode servir.
                    </Text>
                  )}
                </HStack>

                {/* A régua da negociação: o que a peça nos custa normalmente. */}
                {custo != null && !semOferta && (
                  <HStack
                    bg="gray.50"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={2}
                    mb={2}
                    spacing={4}
                    flexWrap="wrap"
                  >
                    <Box>
                      <Text fontSize="10px" color="gray.500">Custo de compra da peça</Text>
                      <Text fontSize="lg" fontWeight="bold" color="#01695E">{formatBRL(custo)}</Text>
                    </Box>
                    {item?.valor_unitario != null && (
                      <Box>
                        <Text fontSize="10px" color="gray.500">Vendido por</Text>
                        <Text fontSize="lg" fontWeight="bold">{formatBRL(item.valor_unitario)}</Text>
                      </Box>
                    )}
                    <Text fontSize="xs" color="gray.500" flex="1" minW="180px">
                      A coluna <b>vs. custo</b> mostra quanto cada oferta está acima ou abaixo
                      desse custo.
                    </Text>
                  </HStack>
                )}

                {travado && (
                  <Alert status="info" borderRadius="md" fontSize="sm" mb={2}>
                    <AlertIcon />
                    {furo?.comprado
                      ? "Este furo já foi comprado."
                      : "Este furo está num pedido pré-formalizado. Tire do pedido para mudar a seleção."}
                  </Alert>
                )}

                {semOferta ? (
                  <Alert status="warning" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Nenhum fornecedor respondeu que tem esta peça.
                  </Alert>
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th w="40px"></Th>
                          <Th>Fornecedor</Th>
                          <Th>Marca oferecida</Th>
                          <Th isNumeric>Preço</Th>
                          <Th isNumeric>vs. custo</Th>
                          <Th>Condições</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {dados.ofertas.map((o) => {
                          const marcada = marcadas.includes(o.id);
                          const cmp = compararCusto(o.preco, custo);
                          return (
                            <Tr
                              key={o.id}
                              bg={marcada ? "blue.50" : undefined}
                              _hover={{ bg: marcada ? "blue.100" : "gray.50" }}
                              cursor={travado ? "default" : "pointer"}
                              onClick={() => { if (!travado) alternar(o.id); }}
                            >
                              <Td px={2}>
                                <Checkbox
                                  isChecked={marcada}
                                  isDisabled={travado}
                                  colorScheme="blue"
                                  onChange={() => alternar(o.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Td>
                              <Td fontSize="xs">
                                <Text fontWeight="medium">{o.fornecedor_nome}</Text>
                                <HStack spacing={1} mt={1}>
                                  {!o.cadastrado && <Badge colorScheme="red" variant="subtle" fontSize="9px">sem cadastro</Badge>}
                                  {o.comprado_aqui && <Badge colorScheme="green" fontSize="9px">comprado</Badge>}
                                  {o.pedido_id && (
                                    <Badge colorScheme={o.pedido_status === "rascunho" ? "orange" : "green"} fontSize="9px">
                                      pedido #{o.pedido_id}
                                    </Badge>
                                  )}
                                </HStack>
                              </Td>
                              <Td fontSize="xs">
                                <Text
                                  fontWeight={furo?.marca_desejada && o.marca_oferecida !== furo.marca_desejada ? "semibold" : "normal"}
                                  color={furo?.marca_desejada && o.marca_oferecida !== furo.marca_desejada ? "orange.600" : undefined}
                                >
                                  {o.marca_oferecida || "—"}
                                </Text>
                                {o.observacoes && (
                                  <Text fontSize="10px" color="gray.500" noOfLines={2}>{o.observacoes}</Text>
                                )}
                              </Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold" color="#01695E">
                                {o.preco != null ? formatBRL(o.preco) : "—"}
                              </Td>
                              <Td fontSize="xs" isNumeric>
                                {cmp ? (
                                  <Tooltip label={cmp.detalhe}>
                                    <Box>
                                      <Badge colorScheme={cmp.cor}>{cmp.texto}</Badge>
                                      <Text fontSize="10px" color="gray.500" mt={1}>
                                        {cmp.dif >= 0 ? "+" : "-"}{formatBRL(Math.abs(cmp.dif))}
                                      </Text>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Text color="gray.400">—</Text>
                                )}
                              </Td>
                              <Td fontSize="xs">
                                <HStack spacing={1} flexWrap="wrap">
                                  {o.prioridade && <Badge colorScheme={corPrioridade(o.prioridade)} fontSize="9px">prio {o.prioridade}</Badge>}
                                  {o.frete_valor > 0 && <Badge fontSize="9px">frete {formatBRL(o.frete_valor)}</Badge>}
                                  {o.prazo_entrega_dias != null && <Badge fontSize="9px">{o.prazo_entrega_dias}d</Badge>}
                                  {o.quantidade_disponivel != null && <Badge colorScheme="blue" fontSize="9px">disp. {o.quantidade_disponivel}</Badge>}
                                </HStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                )}
                {furo?.marca_desejada && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Marca do anúncio: <b>{furo.marca_desejada}</b> — em laranja, quem oferece marca diferente.
                  </Text>
                )}
              </Box>

              {dados.observacoes.length > 0 && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2}>Observações do pedido</Text>
                  <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                    {dados.observacoes.map((o) => (
                      <Box key={o.id} bg="gray.50" borderRadius="md" px={3} py={2}>
                        <Text fontSize="xs" whiteSpace="pre-wrap">{o.observacao}</Text>
                        <Text fontSize="10px" color="gray.400" mt={1}>
                          {o.origem} · {formatDateTime(o.created_at)}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} flexWrap="wrap" justify="end" w="100%">
            <Button size="sm" onClick={onClose}>Fechar</Button>

            {!travado && !semOferta && ANTERIOR[estagio] && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Undo2 size={15} />}
                isLoading={salvando}
                onClick={() => salvar(ANTERIOR[estagio])}
              >
                Voltar
              </Button>
            )}
            {!travado && !semOferta && estagio !== "descartado" && (
              <Tooltip label="O pedido vai para CANCELADA (Status TAGA) com a observação explicando que nenhuma cotação foi aceita.">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<XCircle size={15} />}
                  isLoading={salvando}
                  onClick={() => salvar("descartado")}
                >
                  Nenhuma serve — cancelar pedido
                </Button>
              </Tooltip>
            )}
            {!travado && !semOferta && proximo && (
              <Button
                size="sm"
                colorScheme="green"
                rightIcon={<ArrowRight size={15} />}
                isLoading={salvando}
                isDisabled={marcadas.length === 0}
                onClick={() => salvar(proximo)}
              >
                {rotuloProximo}
              </Button>
            )}
            {!travado && !semOferta && estagio === "aprovado" && (
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                isLoading={salvando}
                isDisabled={marcadas.length === 0}
                onClick={() => salvar("aprovado")}
              >
                Salvar seleção
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function CardStat({ label, valor, cor }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={4} py={3}>
      <Stat>
        <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
        <StatNumber fontSize="2xl" color={cor}>{valor}</StatNumber>
      </Stat>
    </Box>
  );
}

/** O card do quadro: o item da venda que precisa ser coberto. */
function CardFuro({ furo }) {
  const prazo = seloPrazo(furo);
  const iv = furo.item_venda;
  const ofertas = furo.ofertas || [];
  const escolhidas = ofertas.filter((o) => o.selecionada);
  const precos = ofertas.map((o) => o.preco).filter((p) => p != null);
  const menor = precos.length ? Math.min(...precos) : null;
  const noPedido = ofertas.find((o) => o.pedido);
  const marcaPedida = furo.marca_desejada || iv?.marca;
  const custo = iv?.custo_unitario;
  const cmpMenor = compararCusto(menor, custo);

  return (
    <>
      <HStack align="start" spacing={2}>
        {iv?.imagem_url && (
          <Image
            src={iv.imagem_url}
            alt=""
            boxSize="42px"
            objectFit="contain"
            bg="gray.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.100"
            flexShrink={0}
            fallback={<Box boxSize="42px" bg="gray.50" borderRadius="md" />}
          />
        )}
        <Box flex="1" minW={0}>
          <HStack justify="space-between" align="start" spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={2} flex="1" minW={0}>
              {iv?.produto_nome || furo.nome_produto}
            </Text>
            {prazo && (
              <Tooltip label={`Despachar até ${formatDateTime(furo.prazo_envio)}`}>
                <Badge colorScheme={prazo.cor} fontSize="9px" whiteSpace="nowrap">{prazo.texto}</Badge>
              </Tooltip>
            )}
          </HStack>
          <HStack spacing={2} mt={1} flexWrap="wrap">
            <Badge colorScheme="purple" variant="subtle">{furo.codigo_global || `furo #${furo.id}`}</Badge>
            <Badge colorScheme="orange" variant="outline">{furo.quantidade}x</Badge>
            {marcaPedida && (
              <Text fontSize="10px" color="gray.500" noOfLines={1} maxW="90px" title={`Marca do anúncio: ${marcaPedida}`}>
                {marcaPedida}
              </Text>
            )}
          </HStack>
        </Box>
      </HStack>

      {/* De qual pedido de venda veio o furo e quanto a peça nos custa */}
      <Box bg="gray.50" borderRadius="md" px={2} py={1} mt={2}>
        <HStack justify="space-between" spacing={2} align="start">
          <Text fontSize="10px" color="gray.500" noOfLines={1}>
            {furo.venda?.origem || "pedido"} · {furo.pedido_origem || "sem pedido"}
          </Text>
          <VStack spacing={0} align="end" flexShrink={0}>
            {custo != null && (
              <Tooltip label="Custo de compra cadastrado para esta peça">
                <Text fontSize="10px" color="gray.700" whiteSpace="nowrap">
                  custo <b>{formatBRL(custo)}</b>
                </Text>
              </Tooltip>
            )}
            {iv?.valor_unitario != null && (
              <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
                vendido {formatBRL(iv.valor_unitario)}
              </Text>
            )}
          </VStack>
        </HStack>
      </Box>

      <Divider my={2} />

      {escolhidas.length > 0 ? (
        <VStack align="stretch" spacing={1}>
          {escolhidas.slice(0, 3).map((o) => {
            const cmp = compararCusto(o.preco, custo);
            return (
              <HStack key={o.id} justify="space-between" spacing={2}>
                <HStack spacing={1} minW={0}>
                  <CheckCircle size={11} color="#2F855A" />
                  <Text fontSize="11px" noOfLines={1}>
                    {o.fornecedor?.nome}
                    {o.marca_oferecida ? ` · ${o.marca_oferecida}` : ""}
                  </Text>
                </HStack>
                <HStack spacing={1} flexShrink={0}>
                  {cmp && (
                    <Tooltip label={cmp.detalhe}>
                      <Badge colorScheme={cmp.cor} fontSize="9px">{cmp.texto}</Badge>
                    </Tooltip>
                  )}
                  <Text fontSize="11px" fontWeight="bold" color="#01695E" whiteSpace="nowrap">
                    {o.preco != null ? formatBRL(o.preco) : "—"}
                  </Text>
                </HStack>
              </HStack>
            );
          })}
          {escolhidas.length > 3 && (
            <Text fontSize="10px" color="gray.400">+{escolhidas.length - 3} fornecedor(es)</Text>
          )}
        </VStack>
      ) : (
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" color="gray.600">
            {ofertas.length} fornecedor(es) responderam
          </Text>
          {menor != null && (
            <HStack spacing={1} flexShrink={0}>
              {cmpMenor && (
                <Tooltip label={cmpMenor.detalhe}>
                  <Badge colorScheme={cmpMenor.cor} fontSize="9px">{cmpMenor.texto}</Badge>
                </Tooltip>
              )}
              <Text fontSize="sm" fontWeight="bold" color="#01695E" whiteSpace="nowrap">
                de {formatBRL(menor)}
              </Text>
            </HStack>
          )}
        </HStack>
      )}

      {noPedido && (
        <Badge colorScheme="orange" mt={2} fontSize="9px">no pedido #{noPedido.pedido.id}</Badge>
      )}
      <Text fontSize="10px" color="gray.300" mt={2} textAlign="right">
        {escolhidas.length > 0 ? "clique para revisar" : "clique para escolher"}
      </Text>
    </>
  );
}

function CardPedido({ pedido, onAbrir, onLalamove, onAbrirCorrida }) {
  const retirado = pedido.status === "retirado";
  const formalizado = pedido.status !== "rascunho";
  const corridaAtiva = !!pedido.lalamove?.ativa;
  const cor = retirado ? "teal" : formalizado ? "green" : "orange";
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor={`${cor}.200`}
      borderLeftWidth="4px"
      borderLeftColor={`${cor}.400`}
      borderRadius="lg"
      p={3}
      cursor="pointer"
      _hover={{ boxShadow: "sm" }}
      onClick={onAbrir}
    >
      <HStack justify="space-between" align="start">
        <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
          {pedido.fornecedor_nome}
          <Text as="span" fontSize="10px" color="gray.400" fontWeight="normal" ml={1}>#{pedido.id}</Text>
        </Text>
        {retirado
          ? <Badge colorScheme="teal" fontSize="9px"><PackageCheck size={9} style={{ display: "inline" }} /> retirado</Badge>
          : formalizado
            ? <Badge colorScheme="green" fontSize="9px"><CheckCircle size={9} style={{ display: "inline" }} /> ok</Badge>
            : <Badge colorScheme={corPrioridade(pedido.prioridade)} fontSize="9px">prio {pedido.prioridade}</Badge>}
      </HStack>

      <HStack spacing={3} mt={2} fontSize="xs" color="gray.600">
        <HStack spacing={1}><Package size={12} /><Text>{pedido.itens.length} item(ns)</Text></HStack>
        <HStack spacing={1}>
          <Truck size={12} />
          <Text color={pedido.frete_isento ? "green.600" : "gray.600"}>
            {pedido.frete_isento ? "frete grátis" : formatBRL(pedido.frete_cobrado)}
          </Text>
        </HStack>
      </HStack>

      <Divider my={2} />
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.500">Total</Text>
        <Text fontSize="md" fontWeight="bold" color="#01695E">{formatBRL(pedido.total)}</Text>
      </HStack>

      {!formalizado && pedido.frete_gratis_acima > 0 && !pedido.frete_isento && (
        <Text fontSize="10px" color="orange.600" mt={1}>
          faltam {formatBRL(pedido.frete_gratis_acima - pedido.subtotal)} para o frete grátis
        </Text>
      )}
      {formalizado && (
        <Text fontSize="10px" color="gray.400" mt={1}>
          {retirado
            ? `retirado em ${formatDateTime(pedido.retirado_em)}`
            : formatDateTime(pedido.formalizado_em)}
        </Text>
      )}
      {formalizado && pedido.lalamove && (
        <Box mt={2}>
          <SeloCorrida corrida={pedido.lalamove} onAbrir={() => onAbrirCorrida(pedido.lalamove.ordem_id)} />
        </Box>
      )}
      {formalizado && !retirado && !corridaAtiva && (
        <Button
          size="xs"
          colorScheme="teal"
          mt={2}
          w="100%"
          leftIcon={<Truck size={12} />}
          onClick={(e) => { e.stopPropagation(); onLalamove(); }}
        >
          Pedir Lalamove
        </Button>
      )}
      {retirado && pedido.rastreio_link && (
        <Button
          as="a"
          href={pedido.rastreio_link}
          target="_blank"
          rel="noreferrer"
          size="xs"
          variant="link"
          colorScheme="teal"
          mt={1}
          leftIcon={<Link2 size={11} />}
          onClick={(e) => e.stopPropagation()}
        >
          rastrear entrega
        </Button>
      )}
    </Box>
  );
}

function ModalPedido({ pedido, ofertasAprovadas, onClose, onMudou, onSubstituirPedido, onDescartar, onFormalizar, onLalamove }) {
  const toast = useToast();
  const [salvando, setSalvando] = useState(false);
  const [confirmarDesfazer, setConfirmarDesfazer] = useState(false);
  const [obs, setObs] = useState("");
  const [edicao, setEdicao] = useState({});
  const [rastreio, setRastreio] = useState("");
  const [obsRetirada, setObsRetirada] = useState("");

  useEffect(() => {
    setObs(pedido?.observacoes || "");
    setRastreio(pedido?.rastreio_link || "");
    setObsRetirada(pedido?.retirada_observacoes || "");
    setEdicao({});
  }, [pedido]);

  if (!pedido) return null;

  const retirado = pedido.status === "retirado";
  // Fora do rascunho o pedido não é mais editável — formalizado ou já retirado.
  const formalizado = pedido.status !== "rascunho";
  // Ofertas aprovadas do mesmo fornecedor que ainda não entraram em pedido nenhum.
  // Sem cadastro os dois lados têm id nulo, então o nome é quem decide.
  const disponiveis = ofertasAprovadas.filter((o) =>
    pedido.fornecedor_id
      ? o.fornecedor?.id === pedido.fornecedor_id
      : !o.fornecedor?.id && o.fornecedor?.nome === pedido.fornecedor_nome
  );

  // Referência do pedido inteiro: só soma se todo item tiver custo cadastrado.
  const custoItens = pedido.itens.every((i) => i.custo_unitario != null)
    ? pedido.itens.reduce((soma, i) => soma + i.custo_unitario * i.quantidade, 0)
    : null;

  const salvarItem = async (item) => {
    const alterado = edicao[item.id];
    if (!alterado) return;
    setSalvando(true);
    const r = await atualizarItemPedidoFuro(item.id, alterado);
    setSalvando(false);
    if (r?.success) {
      onSubstituirPedido(r.pedido);
      onMudou();
      setEdicao((e) => ({ ...e, [item.id]: undefined }));
      toast({ title: "Item atualizado", status: "success", duration: 2000 });
    } else {
      toast({ title: r?.error || "Erro ao atualizar item", status: "error", isClosable: true });
    }
  };

  const removerItem = async (item) => {
    setSalvando(true);
    const r = await removerItemPedidoFuro(item.id);
    setSalvando(false);
    if (r?.success) {
      onMudou();
      if (r.pedido) onSubstituirPedido(r.pedido);
      else onClose();
      toast({ title: "Item removido — o furo voltou para Aprovados", status: "info", duration: 3000 });
    } else {
      toast({ title: r?.error || "Erro ao remover item", status: "error", isClosable: true });
    }
  };

  const adicionar = async (oferta) => {
    setSalvando(true);
    const r = await adicionarItemPedidoFuro(pedido.id, oferta.id);
    setSalvando(false);
    if (r?.success) {
      onSubstituirPedido(r.pedido);
      onMudou();
      toast({ title: "Item adicionado ao pedido", status: "success", duration: 2000 });
    } else {
      toast({ title: r?.error || "Erro ao adicionar item", status: "error", isClosable: true });
    }
  };

  const salvarObs = async () => {
    setSalvando(true);
    const r = await atualizarPedidoFuro(pedido.id, { observacoes: obs });
    setSalvando(false);
    if (r?.success) {
      onSubstituirPedido(r.pedido);
      onMudou();
      toast({ title: "Observação salva", status: "success", duration: 2000 });
    } else {
      toast({ title: r?.error || "Erro ao salvar", status: "error", isClosable: true });
    }
  };

  const salvarRetirada = async () => {
    setSalvando(true);
    const r = await marcarPedidoFuroRetirado(pedido.id, {
      rastreio_link: rastreio || null,
      retirada_observacoes: obsRetirada || null,
    });
    setSalvando(false);
    if (r?.success) {
      onSubstituirPedido(r.pedido);
      onMudou();
      toast({
        title: retirado ? "Retirada atualizada" : "Pedido marcado como retirado",
        status: "success",
        duration: 3000,
      });
    } else {
      toast({ title: r?.error || "Erro ao salvar a retirada", status: "error", isClosable: true });
    }
  };

  // Volta o pedido para "Formalizados, a retirar" (#2307): mesmo efeito do desfazer da lista.
  const desfazerRetirada = async () => {
    setSalvando(true);
    const r = await desfazerPedidoFuroRetirado(pedido.id);
    setSalvando(false);
    setConfirmarDesfazer(false);
    if (r?.success) {
      onSubstituirPedido(r.pedido);
      onMudou();
      toast({ title: "Retirada desfeita", description: "O pedido voltou para Formalizados, a retirar.", status: "success", duration: 4000 });
    } else {
      toast({ title: r?.error || "Não foi possível desfazer a retirada", status: "error", isClosable: true });
    }
  };

  return (
    <Modal isOpen={!!pedido} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#01695E">
          <HStack spacing={3} flexWrap="wrap">
            <Text>Pedido #{pedido.id} — {pedido.fornecedor_nome}</Text>
            {retirado
              ? <Badge colorScheme="teal">Retirado</Badge>
              : formalizado
                ? <Badge colorScheme="green">Formalizado</Badge>
                : <Badge colorScheme="orange">Rascunho</Badge>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {formalizado && (
            <Alert status={retirado ? "info" : "success"} borderRadius="md" fontSize="sm" mb={4}>
              <AlertIcon />
              {retirado
                ? `Retirado em ${formatDateTime(pedido.retirado_em)} — formalizado em ${formatDateTime(pedido.formalizado_em)}.`
                : `Formalizado em ${formatDateTime(pedido.formalizado_em)}. As vendas já foram confirmadas.`}
            </Alert>
          )}

          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
            <Info label="Itens" valor={pedido.itens.length} />
            {/* Só soma quando todo item tem custo cadastrado — parcial enganaria. */}
            <Info
              label="Custo das peças"
              valor={custoItens != null ? formatBRL(custoItens) : "—"}
              cor="gray.600"
            />
            <Info label="Subtotal" valor={formatBRL(pedido.subtotal)} />
            <Info
              label="Frete"
              valor={pedido.frete_isento ? "grátis" : formatBRL(pedido.frete_cobrado)}
              cor={pedido.frete_isento ? "green.600" : undefined}
            />
            <Info label="Total" valor={formatBRL(pedido.total)} cor="#01695E" />
          </SimpleGrid>

          {!formalizado && pedido.frete_gratis_acima > 0 && !pedido.frete_isento && (
            <Alert status="info" borderRadius="md" fontSize="sm" mb={4}>
              <AlertIcon />
              Faltam {formatBRL(pedido.frete_gratis_acima - pedido.subtotal)} para o frete ficar grátis
              neste fornecedor (isenção acima de {formatBRL(pedido.frete_gratis_acima)}).
            </Alert>
          )}

          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Produto</Th>
                  <Th>Marca</Th>
                  <Th>Pedido</Th>
                  <Th isNumeric>Qtd</Th>
                  <Th isNumeric>Custo</Th>
                  <Th isNumeric>Unitário</Th>
                  <Th isNumeric>vs. custo</Th>
                  <Th isNumeric>Total</Th>
                  {!formalizado && <Th></Th>}
                </Tr>
              </Thead>
              <Tbody>
                {pedido.itens.map((item) => {
                  const alterado = edicao[item.id] || {};
                  const qtd = alterado.quantidade ?? item.quantidade;
                  const preco = alterado.preco_unitario ?? item.preco_unitario;
                  const sujo = edicao[item.id] !== undefined;
                  const cmp = compararCusto(preco, item.custo_unitario);
                  return (
                    <Tr key={item.id} _hover={{ bg: "gray.50" }}>
                      <Td fontSize="xs" maxW="240px">
                        <Text noOfLines={2}>{item.nome_produto}</Text>
                        <Text color="gray.400" fontSize="10px">{item.codigo_global}</Text>
                      </Td>
                      <Td fontSize="xs">{item.marca_oferecida || "—"}</Td>
                      <Td fontSize="xs" color="gray.500">{item.pedido_origem || "—"}</Td>
                      <Td isNumeric>
                        {formalizado ? (
                          <Text fontSize="xs">{item.quantidade}</Text>
                        ) : (
                          <Input
                            size="xs"
                            type="number"
                            min={1}
                            maxW="70px"
                            textAlign="right"
                            value={qtd}
                            onChange={(e) => setEdicao((s) => ({
                              ...s,
                              [item.id]: { ...alterado, quantidade: Number(e.target.value) },
                            }))}
                          />
                        )}
                      </Td>
                      <Td isNumeric fontSize="xs" color="gray.600">
                        {item.custo_unitario != null ? formatBRL(item.custo_unitario) : "—"}
                      </Td>
                      <Td isNumeric>
                        {formalizado ? (
                          <Text fontSize="xs">{formatBRL(item.preco_unitario)}</Text>
                        ) : (
                          <Input
                            size="xs"
                            type="number"
                            step="0.01"
                            min={0}
                            maxW="90px"
                            textAlign="right"
                            value={preco ?? ""}
                            onChange={(e) => setEdicao((s) => ({
                              ...s,
                              [item.id]: { ...alterado, preco_unitario: Number(e.target.value) },
                            }))}
                          />
                        )}
                      </Td>
                      <Td isNumeric>
                        {cmp ? (
                          <Tooltip label={cmp.detalhe}>
                            <Badge colorScheme={cmp.cor} fontSize="10px">{cmp.texto}</Badge>
                          </Tooltip>
                        ) : (
                          <Text fontSize="xs" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="semibold">{formatBRL(qtd * (preco || 0))}</Td>
                      {!formalizado && (
                        <Td>
                          <HStack spacing={1}>
                            {sujo && (
                              <Tooltip label="Salvar alteração">
                                <IconButton
                                  aria-label="Salvar"
                                  size="xs"
                                  colorScheme="green"
                                  icon={<Pencil size={13} />}
                                  isLoading={salvando}
                                  onClick={() => salvarItem(item)}
                                />
                              </Tooltip>
                            )}
                            <Tooltip label="Tirar do pedido">
                              <IconButton
                                aria-label="Remover"
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                icon={<Trash2 size={13} />}
                                isLoading={salvando}
                                onClick={() => removerItem(item)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {!formalizado && disponiveis.length > 0 && (
            <Box mt={4} borderTopWidth="1px" pt={3}>
              <Text fontWeight="bold" fontSize="sm" mb={2}>
                Outras ofertas aprovadas deste fornecedor
              </Text>
              <VStack align="stretch" spacing={1}>
                {disponiveis.map((o) => (
                  <HStack key={o.id} justify="space-between" bg="gray.50" borderRadius="md" px={3} py={2}>
                    <Box minW={0}>
                      <Text fontSize="xs" noOfLines={1}>{o.furo.nome_produto}</Text>
                      <Text fontSize="10px" color="gray.500">
                        {o.marca_oferecida || "sem marca"} · {formatBRL(o.preco)}
                      </Text>
                    </Box>
                    <Button size="xs" colorScheme="orange" variant="outline" isLoading={salvando} onClick={() => adicionar(o)}>
                      Adicionar
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <Box mt={4}>
            <Text fontWeight="bold" fontSize="sm" mb={1}>Observações</Text>
            <Textarea
              size="sm"
              rows={2}
              value={obs}
              isDisabled={formalizado}
              placeholder="Combinado com o fornecedor, condição de pagamento, etc."
              onChange={(e) => setObs(e.target.value)}
            />
            {!formalizado && obs !== (pedido.observacoes || "") && (
              <Button size="xs" mt={2} onClick={salvarObs} isLoading={salvando}>Salvar observação</Button>
            )}
          </Box>

          {/* Última etapa: a mercadoria saiu do fornecedor. */}
          {formalizado && (
            <Box mt={5} borderTopWidth="1px" pt={4}>
              <HStack justify="space-between" mb={1} flexWrap="wrap">
                <Text fontWeight="bold" fontSize="sm">Retirada</Text>
                {retirado && (
                  <Badge colorScheme="teal" fontSize="10px">
                    retirado em {formatDateTime(pedido.retirado_em)}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500" mb={3}>
                {retirado
                  ? "Ajuste o rastreio ou a observação se algo mudou."
                  : "Ao marcar como retirado, o pedido sai da fila de compras e vai para a coluna Retirados."}
              </Text>

              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>Link do rastreio (Lalamove, motoboy)</Text>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none"><Link2 size={14} color="#A0AEC0" /></InputLeftElement>
                    <Input
                      placeholder="https://share.lalamove.com/..."
                      value={rastreio}
                      onChange={(e) => setRastreio(e.target.value)}
                    />
                  </InputGroup>
                  {retirado && pedido.rastreio_link && (
                    <Button
                      as="a"
                      href={pedido.rastreio_link}
                      target="_blank"
                      rel="noreferrer"
                      size="xs"
                      mt={1}
                      variant="link"
                      colorScheme="teal"
                      rightIcon={<ExternalLink size={11} />}
                    >
                      abrir rastreio salvo
                    </Button>
                  )}
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>Observação da retirada</Text>
                  <Textarea
                    size="sm"
                    rows={2}
                    placeholder="Quem retirou, o que veio, divergências, nota do fornecedor..."
                    value={obsRetirada}
                    onChange={(e) => setObsRetirada(e.target.value)}
                  />
                </Box>
              </VStack>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} flexWrap="wrap" justify="end" w="100%">
            <Button size="sm" onClick={onClose}>Fechar</Button>
            {!formalizado && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<Trash2 size={15} />}
                  onClick={() => onDescartar(pedido)}
                >
                  Descartar pedido
                </Button>
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<ShoppingCart size={15} />}
                  onClick={() => onFormalizar(pedido)}
                  isDisabled={pedido.itens.length === 0}
                >
                  Pedido formalizado com o fornecedor
                </Button>
              </>
            )}
            {formalizado && !retirado && (
              <Button
                size="sm"
                colorScheme="teal"
                variant="outline"
                leftIcon={<Truck size={15} />}
                onClick={() => onLalamove(pedido)}
              >
                {pedido.lalamove?.ativa ? "Ver corrida Lalamove" : "Pedir Lalamove"}
              </Button>
            )}
            {retirado && (
              <Button
                size="sm"
                colorScheme="red"
                variant="ghost"
                leftIcon={<Undo2 size={15} />}
                isDisabled={salvando}
                onClick={() => setConfirmarDesfazer(true)}
              >
                Desfazer retirada
              </Button>
            )}
            {formalizado && (
              <Button
                size="sm"
                colorScheme="teal"
                variant={retirado ? "outline" : "solid"}
                leftIcon={<PackageCheck size={15} />}
                isLoading={salvando}
                isDisabled={retirado
                  && rastreio === (pedido.rastreio_link || "")
                  && obsRetirada === (pedido.retirada_observacoes || "")}
                onClick={salvarRetirada}
              >
                {retirado ? "Salvar retirada" : "Pedido retirado"}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>

      <ConfirmDialog
        isOpen={confirmarDesfazer}
        onClose={() => setConfirmarDesfazer(false)}
        onConfirm={desfazerRetirada}
        isLoading={salvando}
        title={`Desfazer a retirada do pedido #${pedido.id}?`}
        body="O pedido volta para Formalizados, a retirar, e o rastreio e a observação da retirada são apagados."
        confirmText="Desfazer retirada"
        confirmColor="red"
        cancelText="Voltar"
      />
    </Modal>
  );
}

function Info({ label, valor, cor }) {
  return (
    <Box bg="gray.50" borderRadius="md" px={3} py={2}>
      <Text fontSize="10px" color="gray.500">{label}</Text>
      <Text fontSize="md" fontWeight="bold" color={cor}>{valor}</Text>
    </Box>
  );
}
